import {Button, InfoTooltip} from '@wandb/components';
import {TooltipProps} from '@wandb/components/dist/components/Tooltip';
import React, {useCallback} from 'react';

import {PanelQuickAddEvent} from '../../../services/analytics/panelQuickAddEvents';
import {useDispatch} from '../../../state/hooks';
import * as PanelBankConfigActions from '../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {getDefaultPanelConfig} from '../../../util/panelbank';
import {maybePluralize} from '../../../util/uihelpers';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsContext} from '../Settings/WorkspaceSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {BULK_ADD_LIMIT} from './constants';
import {QuickAddKeys} from './PanelsQuickAddContext';

type AddBulkButtonProps = {
  searchedKeys: QuickAddKeys;
  keysWithNoPanels: QuickAddKeys;
  trackQuickAddBound: (event: Omit<PanelQuickAddEvent, 'scope'>) => void;
  onBulkAdd?: () => void;
};

export const AddBulkButton = ({
  searchedKeys,
  keysWithNoPanels,
  trackQuickAddBound,
  onBulkAdd,
}: AddBulkButtonProps) => {
  const numKeysInList = searchedKeys.length;
  const numKeysToAdd = keysWithNoPanels.length;
  const isLimitExceeded = numKeysToAdd > BULK_ADD_LIMIT;
  const isSomeKeysAdded = numKeysInList > numKeysToAdd;
  const isAllKeysAdded = numKeysInList > 0 && numKeysToAdd === 0;
  const isDisabled = isLimitExceeded || isAllKeysAdded;

  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {targetSectionRef} = useWorkspaceDrawerContext();
  const {
    workspaceLayout: {sortAlphabetically},
  } = useWorkspaceSettingsContext();

  const dispatch = useDispatch();
  const onAddBulkClick = useCallback(() => {
    if (isDisabled) {
      return;
    }
    trackQuickAddBound({action: 'Add bulk clicked'});
    if (targetSectionRef) {
      const panelConfigs = keysWithNoPanels.map(([key, spec]) =>
        getDefaultPanelConfig(key, spec)
      );
      dispatch(
        PanelBankSectionConfigActions.addPanelsByConfig(
          targetSectionRef,
          panelConfigs,
          sortAlphabetically
        )
      );
    } else {
      dispatch(
        PanelBankConfigActions.addPanelsBySpec(
          panelBankConfigRef,
          keysWithNoPanels.map(([key, spec]) => ({key, spec})),
          sortAlphabetically
        )
      );
    }
    onBulkAdd?.();
  }, [
    dispatch,
    isDisabled,
    keysWithNoPanels,
    onBulkAdd,
    panelBankConfigRef,
    sortAlphabetically,
    targetSectionRef,
    trackQuickAddBound,
  ]);

  if (!numKeysInList) {
    return null;
  }

  const tooltip = getTooltip(isLimitExceeded, isSomeKeysAdded, isAllKeysAdded);

  if (isDisabled) {
    return (
      <InfoTooltip
        id="add-bulk-disabled-info"
        aria-label="Info tooltip"
        size="sm"
        {...tooltip}
      />
    );
  }

  return (
    <Button
      size="sm"
      variant="secondary"
      startIcon="add-new"
      onPress={onAddBulkClick}
      isDisabled={isDisabled}
      tooltip={tooltip}>
      {isSomeKeysAdded && !isDisabled
        ? `Add ${maybePluralize(numKeysToAdd, 'panel', 's', true)}`
        : 'Add all'}
    </Button>
  );
};

function getTooltip(
  isLimitExceeded: boolean,
  isSomeKeysAdded: boolean,
  isAllKeysAdded: boolean
): TooltipProps {
  let text = '';
  if (isLimitExceeded) {
    text = `You may add up to ${BULK_ADD_LIMIT.toLocaleString()} panels at once. Refine your search to limit the results.`;
  } else if (isAllKeysAdded) {
    text = 'All the listed keys already have a panel in this workspace.';
  } else if (isSomeKeysAdded) {
    text =
      'Generate panels for keys that do not already have one in this workspace.';
  }
  return {
    children: text,
    placement: 'top end',
  };
}
